"use client"

import { Component } from "react"

import type { ErrorInfo, ReactNode } from "react"
import type { AppInsightsContextValue } from "./app-insights-provider"

import { isErrorTracked, markErrorTracked } from "./app-insights-global"
import { useAppInsights } from "./app-insights-provider"

/**
 * Class error boundary that reports caught render errors to Application Insights
 * via trackException. Wrap subtrees whose errors you want captured; pass a
 * `fallback` to render after an error. Safe when telemetry is disabled — the
 * injected helpers no-op.
 */

interface Props {
  /** Telemetry sink — injected by the exported wrapper from context. */
  appInsights: AppInsightsContextValue
  /** Rendered after an error is caught. Defaults to nothing. */
  fallback?: ReactNode
  children: ReactNode
}

interface State {
  hasError: boolean
}

class ErrorBoundaryInner extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Already reported by the query-cache handlers (M21) — skip the duplicate.
    if (isErrorTracked(error)) return
    markErrorTracked(error)
    this.props.appInsights.trackException(
      { exception: error },
      { componentStack: info.componentStack ?? "" }
    )
  }

  override render(): ReactNode {
    if (this.state.hasError) return this.props.fallback ?? null
    return this.props.children
  }
}

export function AppInsightsErrorBoundary({
  fallback,
  children,
}: {
  fallback?: ReactNode
  children: ReactNode
}) {
  const appInsights = useAppInsights()
  return (
    <ErrorBoundaryInner appInsights={appInsights} fallback={fallback}>
      {children}
    </ErrorBoundaryInner>
  )
}
